import { z } from 'zod'

export const deviceTokenRequestSchema = z.object({
  token: z.string().min(1),
})

export const deviceTokenResponseSchema = z.object({
  id: z.string().uuid(),
  authUserId: z.string().uuid(),
  token: z.string(),
  createdAt: z.string().datetime(),
})

export const notificationCategorySchema = z.enum([
  'plan_invite',
  'plan_request',
  'plan_participant',
  'plan_update',
  'plan_cancelled',
  'plan_removed',
  'plan_reminder',
  'friend_request',
  'friend_accept',
  'direct_message',
  'plan_message',
  'spot_invite',
  'event_reminder',
  'drop',
  'group_invite',
  'favorite_place',
])

export const notificationActorSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
})

export const notificationGroupDtoSchema = z.object({
  id: z.string().uuid(),
  groupKey: z.string(),
  category: notificationCategorySchema,
  representativeType: z.string(),
  isRead: z.boolean(),
  navigable: z.boolean(),
  planId: z.string().uuid().nullable(),
  conversationId: z.string().uuid().nullable(),
  friendshipId: z.string().uuid().nullable(),
  placeId: z.string().uuid().nullable(),
  dropId: z.string().uuid().nullable(),
  /** The event a Live Room request is about. */
  eventId: z.string().uuid().nullable().optional(),
  /** The title of that event, also after it was deleted; set on Live Room endings. */
  eventTitle: z.string().nullable().optional(),
  actors: z.array(notificationActorSchema),
  actorCount: z.number().int(),
  eventCount: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export const notificationFeedResponseSchema = z.object({
  groups: z.array(notificationGroupDtoSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

export const unreadCountResponseSchema = z.object({
  count: z.number().int(),
})

export const markNotificationsReadRequestSchema = z.union([
  z.object({ groupIds: z.array(z.string().uuid()).min(1) }),
  z.object({ all: z.literal(true) }),
  z.object({ planId: z.string().uuid() }),
])

export const notificationPreferenceDtoSchema = z.object({
  category: notificationCategorySchema,
  pushEnabled: z.boolean(),
})

export const notificationPreferencesResponseSchema = z.object({
  preferences: z.array(notificationPreferenceDtoSchema),
  /** The master switch; off means no push at all, whatever the categories say. */
  allPushEnabled: z.boolean(),
})

export const updateNotificationPreferenceRequestSchema = z.object({
  category: notificationCategorySchema,
  pushEnabled: z.boolean(),
})

export const updateAllNotificationsRequestSchema = z.object({
  pushEnabled: z.boolean(),
})

/** The categories an app has labels for, comma-separated; without it older apps' set is used. */
export const notificationPreferencesQuerySchema = z.object({
  categories: z.string().optional(),
})

export const successResponseSchema = z.object({
  success: z.boolean(),
})

export type DeviceTokenRequest = z.infer<typeof deviceTokenRequestSchema>
export type DeviceTokenResponse = z.infer<typeof deviceTokenResponseSchema>
export type NotificationCategory = z.infer<typeof notificationCategorySchema>
export type NotificationActor = z.infer<typeof notificationActorSchema>
export type NotificationGroupDto = z.infer<typeof notificationGroupDtoSchema>
export type NotificationFeedResponse = z.infer<typeof notificationFeedResponseSchema>
export type UnreadCountResponse = z.infer<typeof unreadCountResponseSchema>
export type MarkNotificationsReadRequest = z.infer<typeof markNotificationsReadRequestSchema>
export type NotificationPreferenceDto = z.infer<typeof notificationPreferenceDtoSchema>
export type NotificationPreferencesResponse = z.infer<typeof notificationPreferencesResponseSchema>
export type UpdateNotificationPreferenceRequest = z.infer<
  typeof updateNotificationPreferenceRequestSchema
>
export type UpdateAllNotificationsRequest = z.infer<typeof updateAllNotificationsRequestSchema>
export type NotificationPreferencesQuery = z.infer<typeof notificationPreferencesQuerySchema>
export type SuccessResponse = z.infer<typeof successResponseSchema>
